import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, ShieldAlert, ShoppingCart, Search, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { PbrMark, PbrNotice } from '../../components/cultivar';
import cultivarService from '../../services/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { Cultivar } from '../../types/cultivar';

interface FormState {
  cultivar_id: number | null;
  cultivar_name: string;
  qty_trees: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  sale_window_start: string;
  sale_window_end: string;
  expected_payment_date: string;
  notes: string;
}

const emptyForm = (): FormState => ({
  cultivar_id: null,
  cultivar_name: '',
  qty_trees: '',
  company_name: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  street: '',
  suburb: '',
  state: '',
  postcode: '',
  country: 'Australia',
  sale_window_start: '',
  sale_window_end: '',
  expected_payment_date: '',
  notes: '',
});

function OrderCreate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [form, setForm] = useState<FormState>(emptyForm());
  const [selectedCultivar, setSelectedCultivar] = useState<Cultivar | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Cultivar[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Optional ?cultivar_id=X auto-select
  useEffect(() => {
    const cid = params.get('cultivar_id');
    if (!cid) return;
    (async () => {
      try {
        const resp = await cultivarService.getCultivar(parseInt(cid, 10));
        if (resp.success && resp.data) {
          setForm((p) => ({ ...p, cultivar_id: resp.data!.id, cultivar_name: resp.data!.name }));
          setSelectedCultivar(resp.data);
        }
      } catch {
        // ignore
      }
    })();
  }, [params]);

  const search = useCallback(async (term: string) => {
    if (!term.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const resp = await cultivarService.searchCultivars({ search: term, limit: 10 });
      if (resp.success && resp.data) setResults(resp.data.cultivars);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(searchTerm), 250);
    return () => clearTimeout(t);
  }, [searchTerm, search]);

  const submit = async () => {
    if (!form.cultivar_id || !form.qty_trees) {
      setError('Cultivar and qty are required.');
      return;
    }
    const qty = parseInt(form.qty_trees, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('qty_trees must be a positive integer.');
      return;
    }
    if (!form.company_name || !form.contact_name || !form.contact_email) {
      setError('Customer company, contact name and email are required.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        cultivar_id: form.cultivar_id,
        qty_trees: qty,
        customer: {
          company_name: form.company_name,
          contact_name: form.contact_name,
          contact_email: form.contact_email,
          contact_phone: form.contact_phone || null,
          address: {
            street: form.street || null,
            suburb: form.suburb || null,
            state: form.state || null,
            postcode: form.postcode || null,
            country: form.country || null,
          },
        },
        sale_window_start: form.sale_window_start || null,
        sale_window_end: form.sale_window_end || null,
        expected_payment_date: form.expected_payment_date || null,
        notes: form.notes || null,
      };
      const resp = await cultivarService.placeOrderingOrder(payload);
      if (!resp.success) throw new Error(resp.error?.message || 'Failed to place order');
      if (!resp.data) throw new Error('Failed to place order');
      navigate(`/cultivar/orders/${(resp.data as { id: number }).id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  if (licenseLoading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold mb-1">Cultivar PBR seat required</h3>
            <p className="text-sm text-gray-600">A cultivar PBR or admin licence seat is required to place orders.</p>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Place cultivar order"
        subtitle="Order on behalf of a final customer. Contracts go out via Adobe Sign once you accept."
        icon={<ShoppingCart className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/cultivar/orders')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Orders
          </Button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded px-4 py-2 text-sm">{error}</div>}

        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold">Cultivar</h3>
            {form.cultivar_id ? (
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                <span className="text-sm">
                  {form.cultivar_name}
                  <PbrMark cultivar={selectedCultivar} />
                </span>
                <button onClick={() => { setForm({ ...form, cultivar_id: null, cultivar_name: '' }); setSelectedCultivar(null); }}
                  className="ml-auto text-gray-500 hover:text-gray-800" aria-label="Clear cultivar">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center border rounded px-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search cultivars you can supply…"
                    className="flex-1 px-2 py-1.5 text-sm focus:outline-none" />
                  {searching && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                </div>
                {results.length > 0 && (
                  <div className="absolute z-10 mt-1 bg-white border rounded shadow w-full max-h-64 overflow-auto">
                    {results.map((c) => (
                      <button key={c.id}
                        onClick={() => { setForm({ ...form, cultivar_id: c.id, cultivar_name: c.name }); setSelectedCultivar(c); setResults([]); setSearchTerm(''); }}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50">
                        <span className="font-medium">{c.name}</span>
                        <PbrMark cultivar={c} />
                        {c.trade_name && <span className="text-gray-500"> · {c.trade_name}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {selectedCultivar && <PbrNotice cultivar={selectedCultivar} />}

        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold">Quantity &amp; sale window</h3>
            <div className="grid grid-cols-3 gap-3">
              <label className="text-sm col-span-1">
                <span className="text-gray-700">Qty trees</span>
                <input type="number" min={1} value={form.qty_trees}
                  onChange={(e) => setForm({ ...form, qty_trees: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm col-span-1">
                <span className="text-gray-700">Sale window start</span>
                <input type="date" value={form.sale_window_start}
                  onChange={(e) => setForm({ ...form, sale_window_start: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm col-span-1">
                <span className="text-gray-700">Sale window end</span>
                <input type="date" value={form.sale_window_end}
                  onChange={(e) => setForm({ ...form, sale_window_end: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm col-span-1">
                <span className="text-gray-700">Expected payment date</span>
                <input type="date" value={form.expected_payment_date}
                  onChange={(e) => setForm({ ...form, expected_payment_date: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold">Final customer</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm col-span-2">
                <span className="text-gray-700">Company name</span>
                <input value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm">
                <span className="text-gray-700">Contact name</span>
                <input value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm">
                <span className="text-gray-700">Contact email</span>
                <input type="email" value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm">
                <span className="text-gray-700">Contact phone</span>
                <input value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm">
                <span className="text-gray-700">Country</span>
                <input value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm col-span-2">
                <span className="text-gray-700">Street</span>
                <input value={form.street}
                  onChange={(e) => setForm({ ...form, street: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm">
                <span className="text-gray-700">Suburb / town</span>
                <input value={form.suburb}
                  onChange={(e) => setForm({ ...form, suburb: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm">
                <span className="text-gray-700">State / postcode</span>
                <div className="mt-1 flex gap-2">
                  <input value={form.state} placeholder="VIC"
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className="w-1/2 border rounded px-2 py-1.5" />
                  <input value={form.postcode} placeholder="3000"
                    onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                    className="w-1/2 border rounded px-2 py-1.5" />
                </div>
              </label>
              <label className="text-sm col-span-2">
                <span className="text-gray-700">Notes (internal)</span>
                <textarea value={form.notes} rows={2}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate('/cultivar/orders')} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Place order
          </Button>
        </div>
      </div>
    </>
  );
}

export default OrderCreate;
